/**
 * Sentinel plus the activity waterfall, stacked.
 *
 * Hosts with a dock split these onto their own tabs. The workspace left
 * column still needs both in one scroll when it is the only live surface.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { Mission, MissionRouteStatus, SentinelAction } from '../../services/missionApi';
import MissionAlertWaterfall from './MissionAlertWaterfall';
import MissionSentinel from './MissionSentinel';
import { RouteOptionPreview } from './map/MissionOptionPreviewLayer';
import { useMissionRunWatch } from './useMissionRunWatch';

export interface MissionLiveTabProps {
  mission: Mission;
  deviceLabel: (deviceId: string) => string;
  onError: (message: string | null) => void;
  onMissionChange: () => void;
  onFocus?: (longitude: number, latitude: number) => void;
  units?: 'km' | 'mi';
  onStatus?: (status: MissionRouteStatus) => void;
  onPreviewOption?: (preview: RouteOptionPreview | null) => void;
}

const MissionLiveTab: React.FC<MissionLiveTabProps> = ({
  mission, deviceLabel, onError, onMissionChange, onFocus, units = 'mi', onStatus,
  onPreviewOption,
}) => {
  const watch = useMissionRunWatch(mission, { onError, onStatus, onMissionChange });

  if (!watch.hasRoute) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Plan a route for this mission to watch it while it runs.
      </Typography>
    );
  }

  return (
    <Box>
      <MissionSentinel
        mission={mission}
        onError={onError}
        onMissionChange={onMissionChange}
        refreshKey={watch.compareKey}
        offRoute={watch.offRoute}
        status={watch.status}
        onPreviewOption={onPreviewOption}
      />

      <MissionAlertWaterfall
        mission={mission}
        events={watch.events}
        deviceLabel={deviceLabel}
        units={units}
        checking={watch.checking}
        emptyHint={watch.running ? 'Nothing to report.' : 'No updates yet.'}
        onFocus={onFocus}
        onAcked={watch.apply}
        onRouteAround={(action: SentinelAction) => { watch.routeAround(action); }}
        routing={watch.routing}
        timeline={watch.status?.timeline}
      />
    </Box>
  );
};

export default MissionLiveTab;